import { lazy, Suspense } from 'react';
import Layout from "./Layout";
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';

// Lazy load all pages except the main Marketing page to improve initial load
const Marketing = lazy(() => import("./Marketing"));
const Careers = lazy(() => import("./Careers"));
const JobDetail = lazy(() => import("./JobDetail"));
const TermsOfService = lazy(() => import("./TermsOfService"));
const PrivacyPolicy = lazy(() => import("./PrivacyPolicy"));
const AccessibilityStatement = lazy(() => import("./AccessibilityStatement"));
const Backoffice = lazy(() => import("./Backoffice"));
const Management = lazy(() => import("./Management"));

const PAGES = {
    Marketing: Marketing,
    Careers: Careers,
    TermsOfService: TermsOfService,
    PrivacyPolicy: PrivacyPolicy,
    AccessibilityStatement: AccessibilityStatement,
} as const;

type PageName = keyof typeof PAGES;

export function _getCurrentPage(url: string): PageName {
    if (url.endsWith('/')) {
        url = url.slice(0, -1);
    }
    let urlLastPart = url.split('/').pop() || '';
    if (urlLastPart.includes('?')) {
        urlLastPart = urlLastPart.split('?')[0];
    }

    const urlParts = url.split('/').filter(Boolean);
    if (urlParts[0]?.toLowerCase() === 'careers' && urlParts[1]) {
        return 'Careers';
    }

    const pageName = Object.keys(PAGES).find(page => page.toLowerCase() === urlLastPart.toLowerCase()) as PageName | undefined;
    return pageName || 'Marketing';
}

// Inner content component used by both client and server entries
export function PagesContent() {
    const location = useLocation();
    const currentPage = _getCurrentPage(location.pathname);

    if (location.pathname === "/backoffice") {
        return (
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
                <Backoffice />
            </Suspense>
        );
    }

    if (location.pathname === "/management") {
        return (
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
                <Management />
            </Suspense>
        );
    }

    return (
        <Layout currentPageName={currentPage}>
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
                <Routes>
                    <Route path="/" element={<Marketing />} />
                    <Route path="/marketing" element={<Marketing />} />
                    <Route path="/careers" element={<Careers />} />
                    <Route path="/careers/:jobId" element={<JobDetail />} />
                    <Route path="/termsofservice" element={<TermsOfService />} />
                    <Route path="/privacypolicy" element={<PrivacyPolicy />} />
                    <Route path="/accessibilitystatement" element={<AccessibilityStatement />} />
                </Routes>
            </Suspense>
        </Layout>
    );
}

export default function Pages() {
    return (
        <Router>
            <PagesContent />
        </Router>
    );
}
